// Firebase-kapcsolat a webes frontendhez.
//
// A Flutter appban a konfiguráció be van drótozva a forrásba
// (lib/commons/services/firebase_service.dart). Itt szándékosan környezeti
// változókból jön: így ugyanaz a build tud az éles projektre, egy dev-projektre
// vagy a helyi emulátorra kötni — és a kulcsok nem a kódban élnek.
//
// A Firebase webes API-kulcsa nem titok (a kliensben amúgy is látható), de a
// projekt megválasztása igenis konfiguráció, nem forráskód.

import { initializeApp, type FirebaseApp } from 'firebase/app';
import {
  getAuth,
  connectAuthEmulator,
  type Auth,
} from 'firebase/auth';
import {
  connectFirestoreEmulator,
  getFirestore,
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  type Firestore,
} from 'firebase/firestore';
import {
  getStorage,
  connectStorageEmulator,
  type FirebaseStorage,
} from 'firebase/storage';
import {
  connectFunctionsEmulator,
  getFunctions,
  type Functions,
} from 'firebase/functions';

function required(name: string, value: string | undefined): string {
  if (!value) {
    throw new Error(
      `Hiányzó környezeti változó: ${name}. ` +
        'Másold le a .env.example fájlt .env néven, és töltsd ki.',
    );
  }
  return value;
}

export const firebaseConfig = {
  apiKey: required('VITE_FIREBASE_API_KEY', import.meta.env.VITE_FIREBASE_API_KEY),
  authDomain: required('VITE_FIREBASE_AUTH_DOMAIN', import.meta.env.VITE_FIREBASE_AUTH_DOMAIN),
  projectId: required('VITE_FIREBASE_PROJECT_ID', import.meta.env.VITE_FIREBASE_PROJECT_ID),
  storageBucket: required('VITE_FIREBASE_STORAGE_BUCKET', import.meta.env.VITE_FIREBASE_STORAGE_BUCKET),
  messagingSenderId: required('VITE_FIREBASE_MESSAGING_SENDER_ID', import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID),
  appId: required('VITE_FIREBASE_APP_ID', import.meta.env.VITE_FIREBASE_APP_ID),
};

export const app: FirebaseApp = initializeApp(firebaseConfig);
export const auth: Auth = getAuth(app);
/// A Firestore HELYI TÁRRAL (4.2).
///
/// Enélkül a belépő képernyő ígérete („offline is használható") nem volt
/// igaz a weben: kapcsolat nélkül a lista üresen jött vissza, az írás pedig
/// hibára futott. A helyi tárral a korábban letöltött adat offline is
/// megjelenik, az írás pedig HELYBEN érvényesül, és a kapcsolat
/// helyreálltakor magától felmegy.
///
/// `persistentMultipleTabManager`: több nyitott fül egyetlen tárat használ.
/// Enélkül a második fülön a tár egyszerűen nem indulna el.
///
/// A tár IndexedDB-t igényel. Privát ablakban, letiltott tárolásnál vagy
/// régi böngészőn ez nem elérhető — ilyenkor VAN app, csak offline-képesség
/// nincs. Ezért a hiba nem szakítja meg az indulást, csak naplózzuk.
function createDb(): Firestore {
  try {
    return initializeFirestore(app, {
      localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
    });
  } catch (error) {
    console.warn(
      '[BlazeData] A helyi Firestore-tár nem indult el, az app offline nem használható.',
      error,
    );
    return getFirestore(app);
  }
}

export const db: Firestore = createDb();
export const storage: FirebaseStorage = getStorage(app);

/// A hívható cloud functionök. A RÉGIÓ nem hagyható el: a függvények
/// `us-central1`-ben futnak, és régió nélkül a kliens máshova hívna.
export const functions: Functions = getFunctions(app, 'us-central1');

/// Igaz, ha a helyi emulátorra kötünk — ilyenkor semmilyen éles adatot nem érünk el.
export const usingEmulator = import.meta.env.VITE_FIREBASE_EMULATOR === 'true';

/// Az emulátor gazdaneve. EXPORTÁLT, mert nem csak ez a modul köt rá:
/// a felhasználó-felvétel egy MÁSODLAGOS appot indít, és annak az Authját
/// ugyanide kell kötni. Amíg ez a Flutter oldalon kimaradt, a másodlagos app
/// az ÉLES projektbe vitte a fiókot, miközben a belépés az emulátorba ment
/// (4.7) — ugyanezt a hibát itt nem ismételjük meg.
export const emulatorHost = import.meta.env.VITE_FIREBASE_EMULATOR_HOST ?? '127.0.0.1';

if (usingEmulator) {
  const host = emulatorHost;
  connectAuthEmulator(auth, `http://${host}:9099`, { disableWarnings: true });
  connectFirestoreEmulator(db, host, 8080);
  connectStorageEmulator(storage, host, 9199);
  connectFunctionsEmulator(functions, host, 5001);
  // Látható jelzés, hogy véletlenül se higgyük élesnek a helyi adatot.
  console.info('[BlazeData] Firebase emulátor használatban — nincs éles adat.');
}
